/*
 * The bare specifiers below resolve through the import map in
 * global-importmap.liquid. Static `import` statements would stop this whole
 * module from executing in browsers without import map support, so the
 * dependencies are loaded dynamically after the support check passes.
 */
let fastdom
let onPrefersReducedMotionChange
let prefersReducedMotion

const IMPORT_MAP_UNSUPPORTED_MESSAGE =
  'Your browser is out of date and parts of this site will not work ' +
  'correctly. Please update your browser.'

/**
 * @returns {boolean}
 */
const supportsImportMaps = () =>
  typeof HTMLScriptElement !== 'undefined' &&
  typeof HTMLScriptElement.supports === 'function' &&
  HTMLScriptElement.supports('importmap')

const CLASS_IS_PLAYING = 'is-playing'
const SECTION_VIDEO_SELECTOR = '[data-section-video]'
const PLAY_BUTTON_SELECTOR = '[data-play-button-video-id]'
const HERO_ZONE_SELECTOR = '.m-page-hero'
const AUTOPLAY_GATE_ROOT_MARGIN = '200px'
// A video must stay inside the viewport this long before it starts: early
// layout is unstable and a far-down video can report intersecting briefly
// before below-fold content pushes it out. Playing in that phantom window
// buffers megabytes of video nobody can see.
const AUTOPLAY_SETTLE_DELAY_MS = 500

/** @type {WeakSet<HTMLVideoElement>} */
const userPausedVideos = new WeakSet()

/**
 * Videos whose playback the user started from the play button. The
 * visibility gate must not pause these on scroll-away — before the gate
 * existed, playback simply continued when the video scrolled out.
 * @type {WeakSet<HTMLVideoElement>}
 */
const userStartedVideos = new WeakSet()

/**
 * @param {HTMLVideoElement} video
 * @returns {boolean}
 */
const isActuallyPlaying = (video) => Boolean(!video.paused && !video.ended)

/**
 * @param {Array<() => void>} ops
 * @returns {void}
 */
const mutateAll = (ops) => {
  if (!ops.length) return
  fastdom.mutate(() => {
    for (let i = 0; i < ops.length; i += 1) ops[i]()
  })
}

/**
 * @param {HTMLButtonElement} button
 * @param {boolean} isPlaying
 * @returns {void}
 */
const setPlayUI = (button, isPlaying) => {
  mutateAll([
    () => button.classList.toggle(CLASS_IS_PLAYING, isPlaying),
    () => button.setAttribute('aria-pressed', String(isPlaying))
  ])
}

/**
 * @param {HTMLVideoElement} video
 * @param {HTMLButtonElement} button
 * @returns {void}
 */
const toggleVideoPlayback = (video, button) => {
  if (isActuallyPlaying(video)) {
    userPausedVideos.add(video)
    video.pause()
    return
  }

  userPausedVideos.delete(video)
  userStartedVideos.add(video)

  const playPromise = video.play()
  if (playPromise && typeof playPromise.catch === 'function') {
    playPromise.catch(() => {
      setPlayUI(button, false)
    })
  }

  setPlayUI(button, true)
}

/**
 * @param {Element} target
 * @param {HTMLElement} root
 * @returns {HTMLButtonElement | null}
 */
const getPlayButton = (target, root) => {
  const el = target.closest(PLAY_BUTTON_SELECTOR)
  if (!el || !root.contains(el) || !(el instanceof HTMLButtonElement)) {
    return null
  }

  return el
}

/**
 * @param {HTMLVideoElement} video
 * @param {HTMLButtonElement} button
 * @returns {void}
 */
const pauseSectionVideo = (video, button) => {
  video.pause()
  video.autoplay = false
  setPlayUI(button, false)
}

/**
 * @param {HTMLVideoElement} video
 * @param {HTMLButtonElement} button
 * @returns {void}
 */
const autoplaySectionVideo = (video, button) => {
  // Re-entering the viewport while the video already plays must not call
  // play() again: on native HLS every play() re-arms Chromium's variant
  // probe, so a fast scroll through the 200px rootMargin produced unbounded
  // repeat calls (a milder echo of the phantom-buffering bug in f3f42b13).
  if (isActuallyPlaying(video)) return

  const playPromise = video.play()

  if (playPromise && typeof playPromise.then === 'function') {
    playPromise
      .then(() => setPlayUI(button, true))
      .catch(() => setPlayUI(button, false))
    return
  }

  setPlayUI(button, isActuallyPlaying(video))
}

/**
 * @param {HTMLVideoElement} video
 * @param {HTMLButtonElement} button
 * @returns {void}
 */
const applyMotionPreference = (video, button) => {
  if (prefersReducedMotion()) {
    pauseSectionVideo(video, button)
    return
  }

  autoplaySectionVideo(video, button)
}

/**
 * @param {HTMLElement} container
 * @returns {void}
 */
const initSectionVideo = (container) => {
  const video = container.querySelector('video')
  if (!video) return

  const videoId = video.dataset.videoId
  if (!videoId) return

  video.muted = true

  const playButton = container.querySelector(
    `[data-play-button-video-id="${CSS.escape(videoId)}"]`
  )

  if (!(playButton instanceof HTMLButtonElement)) return

  const syncPlayUI = () => {
    setPlayUI(playButton, isActuallyPlaying(video))
  }

  container.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null
    if (!target) return

    const button = getPlayButton(target, container)
    if (!button) return

    event.preventDefault()
    event.stopPropagation()
    toggleVideoPlayback(video, button)
  })

  video.addEventListener('play', syncPlayUI)
  video.addEventListener('playing', syncPlayUI)
  video.addEventListener('pause', syncPlayUI)
  video.addEventListener('ended', syncPlayUI)

  // Autoplay section videos only while they are near the viewport.
  // Videos in the hero A/B test zone keep the previous always-autoplay
  // behavior so the running Intelligems experiment is unaffected.
  if (
    container.closest(HERO_ZONE_SELECTOR) ||
    typeof IntersectionObserver === 'undefined'
  ) {
    applyMotionPreference(video, playButton)
  } else {
    let settleTimer = null

    // Chromium's native HLS over-fetches on preload="metadata" (it probes
    // multiple variant playlists and buffers seconds of segments for a
    // paused, off-screen video — measured ~8MB on the homepage). Keep the
    // element at preload="none" (set in the section markup) and only
    // upgrade it when the video is about to play.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            if (settleTimer !== null) continue
            settleTimer = setTimeout(() => {
              settleTimer = null
              // Once the user pressed play or pause, the video is theirs:
              // the gate neither starts nor restarts it.
              if (userPausedVideos.has(video)) return
              if (userStartedVideos.has(video)) return
              // Ambient looping video is motion. Consult the motion
              // preference BEFORE the preload upgrade: preload="auto" on a
              // paused HLS video buffers megabytes, and a reduced-motion
              // user never gets that playback. videos.js gates on the same
              // ordering.
              if (!prefersReducedMotion()) {
                video.preload = 'auto'
                applyMotionPreference(video, playButton)
              }
            }, AUTOPLAY_SETTLE_DELAY_MS)
          } else {
            if (settleTimer !== null) {
              clearTimeout(settleTimer)
              settleTimer = null
            }
            if (userStartedVideos.has(video)) continue
            pauseSectionVideo(video, playButton)
          }
        }
      },
      { rootMargin: AUTOPLAY_GATE_ROOT_MARGIN }
    )
    observer.observe(video)
  }

  onPrefersReducedMotionChange((matches) => {
    if (matches) pauseSectionVideo(video, playButton)
  })
}

const initAllSectionVideos = () => {
  document.querySelectorAll(SECTION_VIDEO_SELECTOR).forEach((container) => {
    if (container instanceof HTMLElement) initSectionVideo(container)
  })
}

const bootstrapSectionVideos = async () => {
  const [fastdomModule, utils] = await Promise.all([
    import('@external/fastdom'),
    import('@theme/utils')
  ])

  fastdom = fastdomModule.default
  onPrefersReducedMotionChange = utils.onPrefersReducedMotionChange
  prefersReducedMotion = utils.prefersReducedMotion

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSectionVideos)
  } else {
    initAllSectionVideos()
  }
}

if (supportsImportMaps()) {
  bootstrapSectionVideos()
} else {
  window.alert(IMPORT_MAP_UNSUPPORTED_MESSAGE)
}
